import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

// Landing is eagerly loaded (Lighthouse entry point)
import Landing from "./pages/Landing";

// Everything else is lazy-loaded to reduce initial bundle / FID
const AppLayout = lazy(() => import("@/components/layout/AppLayout").then(m => ({ default: m.AppLayout })));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Sessions = lazy(() => import("./pages/Sessions"));
const HandEntry = lazy(() => import("./pages/HandEntry"));
const Stats = lazy(() => import("./pages/Stats"));
const AICoach = lazy(() => import("./pages/AICoach"));
const Opponents = lazy(() => import("./pages/Opponents"));
const Settings = lazy(() => import("./pages/Settings"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Install = lazy(() => import("./pages/Install"));
const Admin = lazy(() => import("./pages/Admin"));
const VoiceLog = lazy(() => import("./pages/VoiceLog"));
const Credits = lazy(() => import("./pages/Credits"));
const Coaches = lazy(() => import("./pages/Coaches"));
const CoachDetail = lazy(() => import("./pages/CoachDetail"));
const Players = lazy(() => import("./pages/Players"));
const PlayerDetail = lazy(() => import("./pages/PlayerDetail"));
const Messages = lazy(() => import("./pages/Messages"));
const LiveGames = lazy(() => import("./pages/LiveGames"));
const GameDetail = lazy(() => import("./pages/GameDetail"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard"));
const BookingHistory = lazy(() => import("./pages/BookingHistory"));
const PokerVlogs = lazy(() => import("./pages/PokerVlogs"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Forum = lazy(() => import("./pages/Forum"));
const ForumPost = lazy(() => import("./pages/ForumPost"));
const Subscription = lazy(() => import("./pages/Subscription"));
const PokerLobby = lazy(() => import("./pages/PokerLobby"));
const PlayTable = lazy(() => import("./pages/PlayTable"));
const TournamentDetail = lazy(() => import("./pages/TournamentDetail"));
const Bankroll = lazy(() => import("./pages/Bankroll"));
const SessionDetail = lazy(() => import("./pages/SessionDetail"));

const LazyFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const PublicGameDetail = () => {
  const { session, loading } = useAuth();

  if (loading) return <LazyFallback />;
  if (!session) return <Navigate to="/auth" replace />;

  return (
    <Suspense fallback={<LazyFallback />}>
      <AppLayout>
        <GameDetail />
      </AppLayout>
    </Suspense>
  );
};

const queryClient = new QueryClient();

const ProtectedRoutes = () => {
  const { session, loading } = useAuth();

  if (loading) return <LazyFallback />;
  if (!session) return <Navigate to="/landing" replace />;

  return (
    <Suspense fallback={<LazyFallback />}>
      <AppLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/sessions/:sessionId" element={<SessionDetail />} />
        <Route path="/hand-entry" element={<HandEntry />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/ai-coach" element={<AICoach />} />
        <Route path="/opponents" element={<Opponents />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/voice-log" element={<VoiceLog />} />
        <Route path="/credits" element={<Credits />} />
        <Route path="/coaches" element={<Coaches />} />
        <Route path="/coaches/:coachId" element={<CoachDetail />} />
        <Route path="/players" element={<Players />} />
        <Route path="/players/:userId" element={<PlayerDetail />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/live-games" element={<LiveGames />} />
        <Route path="/live-games/:gameId" element={<GameDetail />} />
        <Route path="/coach-dashboard" element={<CoachDashboard />} />
        <Route path="/booking-history" element={<BookingHistory />} />
        <Route path="/poker-vlogs" element={<PokerVlogs />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/forum/:postId" element={<ForumPost />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/play" element={<PokerLobby />} />
        <Route path="/play/:tableId" element={<PlayTable />} />
        <Route path="/tournaments/:tournamentId" element={<TournamentDetail />} />
        <Route path="/bankroll" element={<Bankroll />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </AppLayout>
    </Suspense>
  );
};

const AuthRoute = () => {
  const { session, loading } = useAuth();
  if (loading) return <LazyFallback />;
  if (session) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<LazyFallback />}>
      <Auth />
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/landing" element={<Landing />} />
            <Route path="/auth" element={<AuthRoute />} />
            <Route path="/reset-password" element={<Suspense fallback={<LazyFallback />}><ResetPassword /></Suspense>} />
            <Route path="/install" element={<Suspense fallback={<LazyFallback />}><Install /></Suspense>} />
            <Route path="/live-games/:gameId" element={<PublicGameDetail />} />
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
